import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { bookingAPI, appointmentAPI, claimAPI, unitAPI, bookingPaymentAPI } from '../../lib/api';
import { formatCurrency, formatDate } from '../../lib/utils';
import { GlassCard, StatusBadge } from '../../components/common/GlassCard';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  FileText, Plus, Upload, DollarSign, CheckCircle, Sparkles,
  Landmark, PenLine, ArrowRight, Eye, Receipt, CreditCard, History, Wallet, Mail, Send,
} from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ALL_STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'pending_payment', label: 'Pending Payment' },
  { value: 'payment_uploaded', label: 'Payment Uploaded' },
  { value: 'payment_verified', label: 'Verified' },
  { value: 'loan_application', label: 'Loan Applied' },
  { value: 'loan_approved', label: 'Loan Approved' },
  { value: 'spa_signed', label: 'SPA Signed' },
  { value: 'completed', label: 'Completed' },
];

const Bookings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');

  // Loan dialog
  const [loanDialog, setLoanDialog] = useState(null);
  const [loanForm, setLoanForm] = useState({ status: 'applied', bank_name: '', loan_amount: '', notes: '' });

  // SPA dialog
  const [spaDialog, setSpaDialog] = useState(null);
  // Payment tracking
  const [paymentDialog, setPaymentDialog] = useState(null);
  const [paymentHistoryDialog, setPaymentHistoryDialog] = useState(null);
  const [paymentHistory, setPaymentHistory] = useState(null);
  const [newPayment, setNewPayment] = useState({ amount: '', payment_method: 'bank_transfer', bank_reference: '', notes: '' });
  const [spaForm, setSpaForm] = useState({ spa_date: '', notes: '' });

  // Upload slip dialog
  const [uploadDialog, setUploadDialog] = useState(null);
  const [paymentSlipFile, setPaymentSlipFile] = useState(null);

  const isAgent = user?.role === 'agent';

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const bookingsRes = await bookingAPI.getBookings();
      setBookings(bookingsRes.data || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDepositAmount = (booking) => {
    const amount = booking.booking_fee_amount || booking.deposit_amount || 0;
    return isNaN(amount) ? 0 : amount;
  };

  const handleUploadPaymentSlip = async (bookingId) => {
    if (!paymentSlipFile) { toast.error('Please select a file'); return; }
    try {
      await bookingAPI.uploadPaymentSlip(bookingId, paymentSlipFile);
      toast.success('Payment slip uploaded');
      setUploadDialog(null);
      setPaymentSlipFile(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload payment slip');
    }
  };

  const handleLoanUpdate = async () => {
    if (!loanDialog) return;
    try {
      await bookingAPI.updateLoanStatus(
        loanDialog,
        loanForm.status,
        loanForm.bank_name,
        parseFloat(loanForm.loan_amount) || 0,
        loanForm.notes
      );
      toast.success(`Loan status updated to ${loanForm.status}`);
      setLoanDialog(null);
      setLoanForm({ status: 'applied', bank_name: '', loan_amount: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update loan status');
    }
  };

  const handleSPAUpdate = async () => {
    if (!spaDialog) return;
    try {
      await bookingAPI.updateSPA(spaDialog, true, spaForm.spa_date, spaForm.notes);
      toast.success('SPA marked as signed!');
      setSpaDialog(null);
      setSpaForm({ spa_date: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update SPA status');
    }
  };

  const handleRecordPayment = async () => {
    if (!paymentDialog || !newPayment.amount) return;
    try {
      const res = await bookingPaymentAPI.recordPayment(paymentDialog, {
        amount: parseFloat(newPayment.amount),
        payment_method: newPayment.payment_method,
        bank_reference: newPayment.bank_reference,
        notes: newPayment.notes,
      });
      toast.success(res.data.message);
      setPaymentDialog(null);
      setNewPayment({ amount: '', payment_method: 'bank_transfer', bank_reference: '', notes: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const openPaymentHistory = async (bookingId) => {
    try {
      const res = await bookingPaymentAPI.getPayments(bookingId);
      setPaymentHistory(res.data);
      setPaymentHistoryDialog(bookingId);
    } catch (error) {
      toast.error('Failed to load payment history');
    }
  };

  const downloadPaymentReceipt = async (bookingId, paymentId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/bookings/${bookingId}/payments/${paymentId}/receipt`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed');
      const blob = await res.blob();
      window.open(URL.createObjectURL(blob), '_blank');
      toast.success('Receipt generated!');
    } catch (error) {
      toast.error('Failed to generate receipt');
    }
  };

  const filteredBookings = bookings.filter((b) => {
    if (activeTab === 'all') return true;
    return b.status === activeTab;
  });

  const getBookingActions = (booking) => {
    const actions = [];
    const s = booking.status;
    const postPaymentStatuses = ['payment_uploaded', 'payment_verified', 'loan_application', 'loan_approved', 'spa_pending', 'spa_signed', 'completed'];
    const hasPaymentOrLater = postPaymentStatuses.includes(s);

    if (s === 'pending_payment' || s === 'draft') {
      actions.push({ label: 'Upload Slip', icon: Upload, variant: 'outline', action: () => setUploadDialog(booking.id) });
    }
    // Receipt & payment tracking available once payment is uploaded
    if (hasPaymentOrLater) {
      actions.push({ label: 'Download Receipt', icon: Receipt, variant: 'outline', color: 'border-emerald-300 text-emerald-700 hover:bg-emerald-50', action: async () => {
        try {
          const token = localStorage.getItem('token');
          const res = await fetch(`${API_BASE}/api/bookings/${booking.id}/receipt`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!res.ok) throw new Error('Failed to generate receipt');
          const blob = await res.blob();
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          toast.success('Receipt generated!');
        } catch (error) {
          toast.error('Failed to generate receipt');
        }
      }});
      // Send Receipt to Buyer via email (available after payment verification)
      const verifiedStatuses = ['payment_verified', 'loan_application', 'loan_approved', 'spa_pending', 'spa_signed', 'completed'];
      if (verifiedStatuses.includes(s)) {
        actions.push({ label: 'Send Receipt', icon: Send, variant: 'outline', color: 'border-blue-300 text-blue-700 hover:bg-blue-50', action: async () => {
          try {
            toast.loading('Sending receipt to buyer...', { id: 'send-receipt' });
            const res = await bookingPaymentAPI.sendReceipt(booking.id);
            toast.success(`Receipt sent to ${res.data.message?.split('to ')[1] || 'buyer'}${res.data.cc?.length ? ` (CC: agent)` : ''}`, { id: 'send-receipt' });
          } catch (error) {
            const msg = error.response?.data?.detail || 'Failed to send receipt';
            toast.error(msg, { id: 'send-receipt' });
          }
        }});
      }
      // Payment tracking actions
      const totalFee = booking.total_booking_fee || booking.booking_fee_amount || 0;
      const totalPaid = booking.total_paid || booking.booking_fee_amount || 0;
      const outstanding = Math.max(0, totalFee - totalPaid);
      if (outstanding > 0) {
        actions.push({ label: 'Record Payment', icon: CreditCard, variant: 'outline', color: 'border-amber-300 text-amber-700 hover:bg-amber-50', action: () => setPaymentDialog(booking.id) });
      }
      actions.push({ label: 'Payment History', icon: History, variant: 'outline', color: 'border-gray-300 text-gray-600 hover:bg-gray-50', action: () => openPaymentHistory(booking.id) });
    }
    if (s === 'payment_verified') {
      actions.push({ label: 'Loan Application', icon: Landmark, variant: 'default', color: 'bg-purple-600 hover:bg-purple-700 text-white', action: () => setLoanDialog(booking.id) });
      actions.push({ label: 'Mark SPA Signed', icon: PenLine, variant: 'outline', color: 'border-indigo-300 text-indigo-700 hover:bg-indigo-50', action: () => setSpaDialog(booking.id) });
    }
    if (s === 'loan_application') {
      actions.push({ label: 'Update Loan', icon: Landmark, variant: 'outline', color: 'border-purple-300 text-purple-700 hover:bg-purple-50', action: () => setLoanDialog(booking.id) });
    }
    if (s === 'loan_approved') {
      actions.push({ label: 'Mark SPA Signed', icon: PenLine, variant: 'default', color: 'bg-indigo-600 hover:bg-indigo-700 text-white', action: () => setSpaDialog(booking.id) });
    }
    if (s === 'spa_signed' || s === 'completed') {
      actions.push({ label: 'Claim Commission', icon: DollarSign, variant: 'default', color: 'bg-emerald-600 hover:bg-emerald-700 text-white', action: () => navigate('/commission-claims') });
    }
    return actions;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#4E50FF]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bookings-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Bookings</h1>
          <p className="text-gray-500 mt-1">
            {isAgent ? 'Manage property bookings' : 'View your bookings'}
          </p>
        </div>
        {isAgent && (
          <Link to="/bookings/create">
            <Button className="bg-[#4E50FF] hover:bg-[#3436CC] text-white" data-testid="new-booking-btn">
              <Sparkles className="w-4 h-4 mr-2" />
              New Booking
            </Button>
          </Link>
        )}
      </div>

      {/* Status Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100/80 flex-wrap h-auto gap-1 p-1">
          {ALL_STATUSES.map((s) => (
            <TabsTrigger key={s.value} value={s.value} className="text-xs sm:text-sm" data-testid={`tab-${s.value}`}>
              {s.label}
              {s.value !== 'all' && (
                <Badge variant="secondary" className="ml-1.5 text-[10px] px-1.5 py-0">
                  {bookings.filter(b => b.status === s.value).length}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredBookings.length > 0 ? (
            <GlassCard className="overflow-hidden p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-gray-100">
                      <TableHead className="text-gray-500 font-semibold">Property / Unit</TableHead>
                      <TableHead className="text-gray-500 font-semibold">Buyer</TableHead>
                      <TableHead className="text-gray-500 font-semibold">Booking Fee</TableHead>
                      <TableHead className="text-gray-500 font-semibold">Status</TableHead>
                      <TableHead className="text-gray-500 font-semibold">Date</TableHead>
                      {isAgent && <TableHead className="text-gray-500 font-semibold">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings.map((booking) => {
                      const actions = isAgent ? getBookingActions(booking) : [];
                      return (
                        <TableRow key={booking.id} className="border-gray-50 hover:bg-gray-50/50" data-testid={`booking-${booking.id}`}>
                          <TableCell>
                            <div>
                              <p className="font-medium text-gray-900">{booking.property?.name || '-'}</p>
                              <p className="text-sm text-gray-500">{booking.unit?.unit_number || booking.manual_unit_number || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div>
                              <p className="font-medium text-gray-900">{booking.buyer_name || '-'}</p>
                              <p className="text-sm text-gray-500">{booking.buyer_phone || booking.buyer_email || '-'}</p>
                            </div>
                          </TableCell>
                          <TableCell className="text-[#4E50FF] font-mono font-semibold">
                            {formatCurrency(getDepositAmount(booking))}
                          </TableCell>
                          <TableCell>
                            <StatusBadge status={booking.status} />
                            {/* Booking Fee Status */}
                            {(booking.total_booking_fee || booking.booking_fee_amount) > 0 && (
                              <div className="mt-1">
                                {(() => {
                                  const totalFee = booking.total_booking_fee || booking.booking_fee_amount || 0;
                                  const totalPaid = booking.total_paid || booking.booking_fee_amount || 0;
                                  const outstanding = Math.max(0, totalFee - totalPaid);
                                  const feeStatus = booking.fee_status || (outstanding <= 0 ? 'fully_paid' : 'partial');
                                  return (
                                    <div className="text-[10px] leading-tight">
                                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full font-medium ${feeStatus === 'fully_paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                                        <Wallet className="w-2.5 h-2.5" />
                                        {feeStatus === 'fully_paid' ? 'Fully Paid' : `Bal: RM ${outstanding.toLocaleString()}`}
                                      </span>
                                    </div>
                                  );
                                })()}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-gray-500 text-sm">
                            {formatDate(booking.created_at)}
                          </TableCell>
                          {isAgent && (
                            <TableCell>
                              <div className="flex items-center gap-2 flex-wrap">
                                {actions.map((act, idx) => (
                                  <Button
                                    key={idx}
                                    size="sm"
                                    variant={act.variant}
                                    className={act.color || ''}
                                    onClick={act.action}
                                    data-testid={`action-${act.label.toLowerCase().replace(/\s+/g, '-')}-${booking.id}`}
                                  >
                                    <act.icon className="w-3.5 h-3.5 mr-1" />
                                    {act.label}
                                  </Button>
                                ))}
                                {actions.length === 0 && (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </GlassCard>
          ) : (
            <GlassCard className="text-center py-12">
              <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Bookings</h3>
              <p className="text-gray-500">
                {activeTab === 'all' ? 'No bookings yet' : `No ${activeTab.replace(/_/g, ' ')} bookings`}
              </p>
              {isAgent && (
                <Link to="/bookings/create">
                  <Button className="mt-4 bg-[#4E50FF] hover:bg-[#3436CC] text-white">
                    <Plus className="w-4 h-4 mr-2" />Create Your First Booking
                  </Button>
                </Link>
              )}
            </GlassCard>
          )}
        </TabsContent>
      </Tabs>

      {/* Upload Payment Slip Dialog */}
      <Dialog open={!!uploadDialog} onOpenChange={(open) => { if (!open) { setUploadDialog(null); setPaymentSlipFile(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Payment Slip</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <Input
              type="file"
              accept="image/*,.pdf"
              onChange={(e) => setPaymentSlipFile(e.target.files[0])}
              data-testid="upload-slip-input"
            />
            <Button
              onClick={() => handleUploadPaymentSlip(uploadDialog)}
              className="w-full bg-[#4E50FF] hover:bg-[#3436CC] text-white"
              disabled={!paymentSlipFile}
              data-testid="upload-slip-submit"
            >
              <Upload className="w-4 h-4 mr-2" />Upload
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Loan Application Dialog */}
      <Dialog open={!!loanDialog} onOpenChange={(open) => { if (!open) setLoanDialog(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="w-5 h-5 text-purple-600" />
              Loan Application Update
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label>Loan Status *</Label>
              <Select value={loanForm.status} onValueChange={(v) => setLoanForm({ ...loanForm, status: v })}>
                <SelectTrigger data-testid="loan-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="applied">Applied</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bank Name</Label>
              <Input
                value={loanForm.bank_name}
                onChange={(e) => setLoanForm({ ...loanForm, bank_name: e.target.value })}
                placeholder="e.g., Maybank, CIMB, Public Bank"
                data-testid="loan-bank-input"
              />
            </div>
            <div>
              <Label>Loan Amount (RM)</Label>
              <Input
                type="number"
                value={loanForm.loan_amount}
                onChange={(e) => setLoanForm({ ...loanForm, loan_amount: e.target.value })}
                placeholder="e.g., 500000"
                data-testid="loan-amount-input"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={loanForm.notes}
                onChange={(e) => setLoanForm({ ...loanForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                data-testid="loan-notes-input"
              />
            </div>
            <Button
              onClick={handleLoanUpdate}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="loan-submit-btn"
            >
              Update Loan Status
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* SPA Signed Dialog */}
      <Dialog open={!!spaDialog} onOpenChange={(open) => { if (!open) setSpaDialog(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="w-5 h-5 text-indigo-600" />
              Mark SPA as Signed
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-sm text-indigo-700">
              This will mark the Sales & Purchase Agreement as signed. Once signed, you can submit your commission claim.
            </div>
            <div>
              <Label>SPA Date</Label>
              <Input
                type="date"
                value={spaForm.spa_date}
                onChange={(e) => setSpaForm({ ...spaForm, spa_date: e.target.value })}
                data-testid="spa-date-input"
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={spaForm.notes}
                onChange={(e) => setSpaForm({ ...spaForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                data-testid="spa-notes-input"
              />
            </div>
            <Button
              onClick={handleSPAUpdate}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="spa-submit-btn"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Confirm SPA Signed
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Record Payment Dialog */}
      <Dialog open={!!paymentDialog} onOpenChange={(open) => { if (!open) setPaymentDialog(null); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              Record Booking Fee Payment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {paymentDialog && (() => {
              const b = bookings.find(b => b.id === paymentDialog);
              const totalFee = b?.total_booking_fee || b?.booking_fee_amount || 0;
              const totalPaid = b?.total_paid || b?.booking_fee_amount || 0;
              const outstanding = Math.max(0, totalFee - totalPaid);
              return (
                <div className="bg-amber-50 border border-amber-100 rounded-lg p-3 text-sm space-y-1">
                  <div className="flex justify-between"><span className="text-gray-600">Total Booking Fee:</span><span className="font-medium">RM {totalFee.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Already Paid:</span><span className="text-emerald-600 font-medium">RM {totalPaid.toLocaleString()}</span></div>
                  <div className="flex justify-between border-t border-amber-200 pt-1"><span className="text-amber-700 font-medium">Outstanding:</span><span className="text-amber-700 font-bold">RM {outstanding.toLocaleString()}</span></div>
                </div>
              );
            })()}
            <div>
              <Label>Payment Amount (RM) *</Label>
              <Input type="number" step="100" value={newPayment.amount} onChange={(e) => setNewPayment({ ...newPayment, amount: e.target.value })} placeholder="e.g., 2500" data-testid="record-payment-amount" />
            </div>
            <div>
              <Label>Payment Method *</Label>
              <Select value={newPayment.payment_method} onValueChange={(v) => setNewPayment({ ...newPayment, payment_method: v })}>
                <SelectTrigger data-testid="record-payment-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="online_banking">Online Banking</SelectItem>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Bank Reference</Label>
              <Input value={newPayment.bank_reference} onChange={(e) => setNewPayment({ ...newPayment, bank_reference: e.target.value })} placeholder="Transaction reference number" data-testid="record-payment-ref" />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea value={newPayment.notes} onChange={(e) => setNewPayment({ ...newPayment, notes: e.target.value })} placeholder="e.g., Partial payment before SPA" data-testid="record-payment-notes" />
            </div>
            <Button onClick={handleRecordPayment} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="record-payment-submit" disabled={!newPayment.amount || parseFloat(newPayment.amount) <= 0}>
              <CreditCard className="w-4 h-4 mr-2" />Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment History Dialog */}
      <Dialog open={!!paymentHistoryDialog} onOpenChange={(open) => { if (!open) { setPaymentHistoryDialog(null); setPaymentHistory(null); }}}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="w-5 h-5 text-gray-600" />
              Booking Fee Payment History
            </DialogTitle>
          </DialogHeader>
          {paymentHistory && (
            <div className="space-y-4 mt-2">
              <div className="grid grid-cols-3 gap-3 text-center">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Total Fee</p>
                  <p className="text-lg font-bold text-gray-900">RM {(paymentHistory.total_booking_fee || 0).toLocaleString()}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500">Total Paid</p>
                  <p className="text-lg font-bold text-emerald-600">RM {(paymentHistory.total_paid || 0).toLocaleString()}</p>
                </div>
                <div className={`rounded-lg p-3 ${paymentHistory.outstanding_balance > 0 ? 'bg-amber-50' : 'bg-emerald-50'}`}>
                  <p className="text-xs text-gray-500">Outstanding</p>
                  <p className={`text-lg font-bold ${paymentHistory.outstanding_balance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                    RM {(paymentHistory.outstanding_balance || 0).toLocaleString()}
                  </p>
                </div>
              </div>
              {paymentHistory.payments?.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead className="text-xs">#</TableHead>
                        <TableHead className="text-xs">Date</TableHead>
                        <TableHead className="text-xs">Amount</TableHead>
                        <TableHead className="text-xs">Method</TableHead>
                        <TableHead className="text-xs">Reference</TableHead>
                        <TableHead className="text-xs">Receipt</TableHead>
                        <TableHead className="text-xs">Email</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {paymentHistory.payments.map((p, i) => (
                        <TableRow key={p.id}>
                          <TableCell className="text-xs font-medium">{i + 1}</TableCell>
                          <TableCell className="text-xs">{p.created_at?.substring(0, 10)}</TableCell>
                          <TableCell className="text-xs font-medium text-emerald-600">RM {p.amount?.toLocaleString()}</TableCell>
                          <TableCell className="text-xs">{(p.payment_method || '').replace(/_/g, ' ')}</TableCell>
                          <TableCell className="text-xs text-gray-500">{p.bank_reference || '-'}</TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" className="text-xs h-7 text-blue-600 hover:text-blue-800" onClick={() => downloadPaymentReceipt(paymentHistoryDialog, p.id)} data-testid={`download-receipt-${p.id}`}>
                              <Receipt className="w-3 h-3 mr-1" />PDF
                            </Button>
                          </TableCell>
                          <TableCell>
                            <Button size="sm" variant="ghost" className="text-xs h-7 text-emerald-600 hover:text-emerald-800" data-testid={`send-receipt-${p.id}`} onClick={async () => {
                              try {
                                toast.loading('Sending...', { id: `send-pr-${p.id}` });
                                const res = await bookingPaymentAPI.sendPaymentReceipt(paymentHistoryDialog, p.id);
                                toast.success(`Sent to buyer${res.data.cc?.length ? ' (CC: agent)' : ''}`, { id: `send-pr-${p.id}` });
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to send', { id: `send-pr-${p.id}` });
                              }
                            }}>
                              <Send className="w-3 h-3 mr-1" />Send
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className="text-sm text-gray-400 text-center py-4">No payment records found</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Bookings;
